import { useQuery, useMutation } from '@apollo/client';
import {
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  PersonAdd as SignupIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { GET_MEMBER_INVITE_BY_TOKEN_QUERY, ACCEPT_MEMBER_INVITE_MUTATION } from '../services/graphql';

export default function MemberInvite() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [error, setError] = useState('');
  const [acceptStatus, setAcceptStatus] = useState<'idle' | 'accepted'>('idle');

  // Check if we returned from login/signup
  const fromAuth = searchParams.get('from') === 'auth';

  // Fetch invite details to check status
  const { data, loading, error: queryError, refetch } = useQuery(GET_MEMBER_INVITE_BY_TOKEN_QUERY, {
    variables: { token },
    skip: !token,
  });

  const invite = data?.getMemberInviteByToken;

  const [acceptInvite, { loading: acceptLoading }] = useMutation(ACCEPT_MEMBER_INVITE_MUTATION, {
    onCompleted: () => {
      setAcceptStatus('accepted');
      refetch(); // Refresh invite status
    },
    onError: (err) => {
      const code = err.graphQLErrors?.[0]?.extensions?.code;
      switch (code) {
        case 'INVITE_EXPIRED':
          setError('This invitation has expired.');
          break;
        case 'EMAIL_MISMATCH':
          setError(`This invitation is for ${invite?.email}. Please sign in with that email address.`);
          break;
        case 'ALREADY_ACCEPTED':
          setAcceptStatus('accepted');
          break;
        default:
          setError(err.message);
      }
    },
  });

  // Auto-accept when authenticated and returning from login/signup
  useEffect(() => {
    if (isAuthenticated && token && fromAuth && invite) {
      // Check if email matches
      if (user?.email.toLowerCase() !== invite.email.toLowerCase()) {
        setError(`This invitation is for ${invite.email}. You are signed in as ${user?.email}.`);
        return;
      }

      // Check if already accepted
      if (invite.status === 'accepted') {
        setAcceptStatus('accepted');
        return;
      }

      // Auto-accept
      handleAccept();
    }
  }, [isAuthenticated, token, fromAuth, invite, user]);

  const handleAccept = async () => {
    if (!token) return;

    setError('');
    await acceptInvite({
      variables: {
        input: { token },
      },
    });
  };

  const handleLogin = () => {
    navigate(`/login?redirect=${encodeURIComponent(`/member-invite/${token}?from=auth`)}`);
  };

  const handleSignup = () => {
    navigate(`/signup?redirect=${encodeURIComponent(`/member-invite/${token}?from=auth`)}`);
  };

  const handleGoToVault = () => {
    navigate('/vault');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (queryError || !invite) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="xs">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
              Invalid Invitation
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This invitation link is invalid or has expired.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Check if invite is expired
  const isExpired = invite.expiresAt && new Date(invite.expiresAt).getTime() < Date.now();
  const isAccepted = invite.status === 'accepted' || acceptStatus === 'accepted';

  if (isExpired && !isAccepted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="xs">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
              Invitation Expired
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This invitation has expired. Please ask for a new invitation.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show accepted state
  if (isAccepted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="xs">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>

            <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
              Invitation Accepted!
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              You have successfully accepted the member invitation. You can now access your vault.
            </Typography>

            <Chip label="Accepted" color="success" sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="contained" size="large" onClick={handleGoToVault}>
                Go to Vault
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Main invitation view
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'primary.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <PersonAddIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>

          <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
            You've Been Invited
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You've been invited to join as a <strong>{invite.role}</strong>.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isAuthenticated ? (
            // User is logged in
            <>
              {user?.email.toLowerCase() === invite.email.toLowerCase() ? (
                // Correct user - show accept button
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleAccept}
                  disabled={acceptLoading}
                >
                  {acceptLoading ? 'Accepting...' : 'Accept Invitation'}
                </Button>
              ) : (
                // Wrong user logged in
                <Alert severity="warning">
                  This invitation is for <strong>{invite.email}</strong>. 
                  You are logged in as <strong>{user?.email}</strong>.
                  Please log out and sign in with the correct account.
                </Alert>
              )}
            </>
          ) : (
            // User is not logged in
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Sign in or create an account with <strong>{invite.email}</strong> to accept this invitation.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<SignupIcon />}
                  onClick={handleSignup}
                >
                  Create Account
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<LoginIcon />}
                  onClick={handleLogin}
                >
                  Sign In
                </Button>
              </Box>
            </>
          )}

          {invite.expiresAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
              This invitation expires on {new Date(invite.expiresAt).toLocaleDateString()}
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
